import { Badge } from "@/components/ui/badge";
import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";

interface VersionBadgeProps {
  /** Version number */
  version: number;
  /** Renders a range (`v5–v7`) when set and different from `version`. */
  lastVersion?: number;
  /** Whether the badge is clickable (has multiple versions) */
  hasHistory?: boolean;
  /** Whether the version list is currently expanded (toggles chevron) */
  isExpanded?: boolean;
  /** Callback when badge is clicked */
  onClick?: () => void;
  /** Accessible action name when the badge opens version history. */
  ariaLabel?: string;
  /** White fill with a grey border, for surfaces whose own background (a dark
   *  glass menu, a tinted row) would otherwise show through the outline. */
  filled?: boolean;
  /** Additional CSS classes */
  className?: string;
}

/**
 * VersionBadge - Atom component
 *
 * Displays version number with "v" prefix (e.g., "v1", "v2").
 * When `hasHistory` is true, the badge becomes clickable and exposes an
 * expand/collapse chevron based on `isExpanded`.
 */
export function VersionBadge({
  version,
  lastVersion,
  hasHistory,
  isExpanded,
  onClick,
  ariaLabel,
  filled,
  className,
}: VersionBadgeProps) {
  const isClickable = hasHistory && onClick;

  return (
    <Badge
      variant="outline"
      data-version-trigger={isClickable ? "" : undefined}
      role={isClickable ? "button" : undefined}
      tabIndex={isClickable ? 0 : undefined}
      aria-label={isClickable ? ariaLabel : undefined}
      className={cn(
        // Non-clickable single-version badge is a circle; the clickable
        // history badge keeps a pill shape to fit its chevron.
        "h-6 shrink-0 justify-center gap-1 transition-colors duration-150",
        isClickable
          ? "min-w-[60px] cursor-pointer hover:border-primary hover:bg-primary/10"
          : "w-6 p-0",
        isExpanded && "border-primary bg-primary/10",
        filled && "border-neutral-300 bg-white text-neutral-700 hover:bg-white",
        filled && isExpanded && "border-primary",
        className
      )}
      onClick={
        isClickable
          ? (e) => {
              // preventDefault too: inside a <label> or a menu item the click
              // would otherwise also tick the row's checkbox.
              e.preventDefault();
              e.stopPropagation();
              onClick();
            }
          : undefined
      }
      onKeyDown={
        isClickable
          ? (event) => {
              if (event.key !== "Enter" && event.key !== " ") return;
              event.preventDefault();
              event.stopPropagation();
              onClick();
            }
          : undefined
      }
    >
      <span>
        v{version}
        {lastVersion !== undefined && lastVersion !== version && `–v${lastVersion}`}
      </span>
      {isClickable && (
        <ChevronDown
          className={cn(
            "h-3 w-3 transition-transform duration-150 ease-out",
            isExpanded && "rotate-180"
          )}
        />
      )}
    </Badge>
  );
}
